/****************
 * Seeing the product as somebody else sees it.
 *
 * Guillaume, 2026-09-06: "I should have some sort of section in that page where
 * I can see HIS UI and see what this user is seeing."
 *
 * The reason this is worth building is not convenience. Every figure on the
 * admin page is a SECOND implementation of something the user already has a
 * view of, and on 2026-09-05 five of one account's sessions disagreed between
 * the two -- different definitions of "placed", "skipped" and "realised", each
 * written separately and drifted apart. A support view that renders the user's
 * OWN screens cannot drift from them, because there is nothing to drift: it is
 * the same components reading the same endpoints.
 *
 * So this is deliberately not a rebuild of the session pages inside the admin
 * drawer. It is the real app, at the real route, in an iframe, with every
 * request identified as that user.
 *
 * ── How the identity is switched ────────────────────────────────────────────
 *
 * `fetchApi` sends `X-User-ID` from `window.__clerk_user_id`. A `?viewAs=` in
 * the URL replaces it for the life of that document, which is why the iframe
 * works: it is a separate document, so the parent's own identity is untouched
 * and closing the drawer ends the impersonation with the frame.
 *
 * ── It is READ ONLY, enforced centrally ─────────────────────────────────────
 *
 * A request that is not a GET or a HEAD is refused while this is active, in
 * fetchApi, where every call passes through. Not by hiding buttons -- hidden
 * buttons are a promise, a thrown error is a guarantee -- because the failure
 * mode here is stopping somebody's live session or starting a trade on their
 * money while looking at their screen.
 *
 * ── What it does NOT do ─────────────────────────────────────────────────────
 *
 * It grants no access that did not already exist. The backend takes the user
 * from a header on an endpoint with no auth on it yet; anyone who can open the
 * admin page can already read any account with one curl. This surfaces that
 * reality rather than widening it, and it will inherit whatever gate B-20 puts
 * on the admin routes, because it is the same routes.
 ****************/

const PARAM = 'viewAs';

/*
 * Read once, at module load, from the URL this document was opened with.
 *
 * Not reactive on purpose. An impersonation that could change under a rendered
 * tree is one that can show account A's numbers under account B's name for a
 * frame, which is the exact class of mistake this exists to prevent. A new
 * subject means a new document.
 */
const viewAsUserId: string | null = (() => {
  try {
    const value = new URLSearchParams(window.location.search).get(PARAM);
    return value && value.trim() ? value.trim() : null;
  } catch {
    return null; // no URL to read (SSR, tests): not impersonating
  }
})();

/** The user this document is impersonating, or null when it is not. */
export function getViewAsUserId(): string | null {
  return viewAsUserId;
}

/** True while this document is showing somebody else's account. */
export function isViewingAsSomeoneElse(): boolean {
  return viewAsUserId !== null;
}

/** The URL that opens `route` as `userId`. */
export function viewAsUrl(route: string, userId: string): string {
  const sep = route.includes('?') ? '&' : '?';
  // Under the app's own base, so the /preview build opens /preview pages (10-24).
  const base = (import.meta.env.BASE_URL || '/').replace(/\/$/, '');
  return `${base}${route}${sep}${PARAM}=${encodeURIComponent(userId)}`;
}

/**
 * Thrown when something tries to change data while impersonating.
 *
 * Its own type so a caller can tell "you may not do this here" apart from a
 * request the server refused.
 */
export class ReadOnlyViewError extends Error {
  constructor(endpoint: string) {
    super(
      `Refused to send a write to ${endpoint} while viewing another user's account. ` +
        `This view is read-only.`,
    );
    this.name = 'ReadOnlyViewError';
  }
}
